import { useRef, useState, useCallback, useEffect } from "react"
import { useDispatch } from "react-redux"
import { MyDispatch, MySelector } from "../../redux/store"
import { setAISearchLoading, setAISearchResults } from "../../redux/utilSlice"
import { searchPhotosByFace } from "../../api"
import { toast } from "sonner"
import faceUploadIcon from "../../assets/Face_Upload.svg"
import LayoutWrapper from "../Photos/LayoutWrapper"
import camera from "../../assets/camera.svg";

const AISearchInterface = ({
  setFolderAndReset
}: {
  setFolderAndReset: (name: string) => void
}) => {
  const dispatch = useDispatch<MyDispatch>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [cameraLoading, setCameraLoading] = useState(false)

  const { aiSearchData } = MySelector((state) => state.util)
  const { eventId } = MySelector((state) => state.event)
  const { theme } = MySelector((state) => state.theme)

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
    }
  }, [stream])

  const handleFileSelect = useCallback((file: File) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file)
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    } else {
      toast.error('Please select a valid image file')
    }
  }, [])

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const startCamera = async () => {
    setCameraLoading(true)
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      })
      
      setStream(mediaStream)
      setShowCamera(true)
      
      // Wait for the next frame to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream
          videoRef.current.play().then(() => {
            setCameraLoading(false)
          }).catch(err => {
            console.error('Error playing video:', err)
            setCameraLoading(false)
            toast.error('Error starting camera preview')
          })
        }
      }, 100)
    } catch (error) {
      console.error('Error accessing camera:', error)
      setCameraLoading(false)
      toast.error('Unable to access camera. Please check permissions.')
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setShowCamera(false)
    setCameraLoading(false)
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')
      
      if (context && video.videoWidth > 0 && video.videoHeight > 0) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        
        // Flip the image horizontally to match the mirrored preview
        context.scale(-1, 1)
        context.drawImage(video, -canvas.width, 0)
        context.scale(-1, 1) // Reset the scale
        
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' })
            handleFileSelect(file)
            stopCamera()
          }
        }, 'image/jpeg', 0.8)
      } else {
        toast.error('Camera not ready. Please wait a moment and try again.')
      }
    }
  }

  const handleSearch = async () => {
    if (!selectedFile || !eventId) return

    dispatch(setAISearchLoading(true))
    try {
      const results = await searchPhotosByFace(eventId, selectedFile)
      
      // Handle edge case where response.data.body might be null or not an array
      if (results && Array.isArray(results) && results.length > 0) {
        dispatch(setAISearchResults(results))
        toast.success(`Found ${results.length} photos`)
      } else {
        dispatch(setAISearchResults([]))
        toast.info('No photos found with this face. Ensure your face is well lit and clear.')
      }
    } catch (error) {
      console.error('AI search error:', error)
      dispatch(setAISearchResults([]))
      toast.error('Search failed. Please try again.')
    } finally {
      dispatch(setAISearchLoading(false))
    }
  }

  const handleClearSelection = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
  }

  // Show results if available, otherwise show upload interface
  if (aiSearchData.results.length > 0) {
    return (
      <div>
        {/* Search results header */}
        <div className="flex flex-col sm:flex-row items-center justify-center mb-6 px-4">
          <div className="flex items-center gap-4 mb-4 sm:mb-0">
            {selectedFile && previewUrl && (
              <img
                src={previewUrl}
                alt="Search query"
                className="w-12 h-12 object-cover rounded-lg"
              />
            )}
            <div>
              <p className="text-sm font-semibold text-newDarkBlack dark:text-white">
                Results
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Found {aiSearchData.results.length} photos
              </p>
            </div>
            <button
                onClick={() => {
                  dispatch(setAISearchResults([]))
                  handleClearSelection()
                }}
                className=" border border-gray-300 dark:border-golden dark:bg-golden text-black px-2 py-1 rounded-lg hover:border-gray-600 transition-colors font-medium text-sm"
            >
              New Search
            </button>
          </div>
        </div>

        {/* Display results using existing photo layout */}
        <LayoutWrapper />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <div className="max-w-md w-full">
        {showCamera ? (
          /* Camera Interface */
          <div className="flex flex-col items-center">
            <div className="relative mb-4 bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                width="320"
                height="240"
                className="w-80 h-60 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-black"
                style={{ transform: 'scaleX(-1)' }} // Mirror effect for selfie
              />
              <canvas ref={canvasRef} className="hidden" />

              {/* Face positioning guide */}
              {!cameraLoading && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative">
                    {/* Face oval guide */}
                    <div
                      className="border-2 border-white/80 rounded-full bg-transparent"
                      style={{
                        width: '180px',
                        height: '220px',
                        boxShadow: '0 0 0 1000px rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Loading overlay */}
              {cameraLoading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                  <div className="text-white text-center">
                    <svg className="animate-spin h-8 w-8 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm">Starting camera...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={stopCamera}
                className="border border-gray-300 dark:border-golden dark:text-golden text-black px-4 py-2 rounded-lg hover:border-gray-600 transition-colors font-medium text-sm"
              >
                Cancel
              </button>

              <button
                onClick={capturePhoto}
                disabled={cameraLoading}
                className="bg-gray-800 dark:border-golden dark:bg-golden dark:text-black text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 text-sm"
              >
                Capture
              </button>
            </div>
          </div>
        ) : !selectedFile ? (
          <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <img
              src={faceUploadIcon}
              alt="Profile"
              className="w-16 h-16 mb-4 opacity-60"
            />
            <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
              Add a selfie to find all your images
            </p>
            
            {/* Camera and Upload Options */}
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              {eventId !== "f64d5c31-2e1a-46a3-a993-21f7d788b2cb" && eventId !== "90cc4a0e-2ca4-4a4c-987f-592bf7dd2bef" &&  eventId !== "60aee284-6758-45ac-9f33-724c75fba84d" && eventId !== "3b012018-91cb-4fc3-9ccb-76515dabd681" && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 border border-gray-300 text-black dark:border-golden dark:text-golden px-2 py-2 rounded-lg hover:border-gray-600 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  {/*<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">*/}
                  {/*  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>*/}
                  {/*  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>*/}
                  {/*  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>*/}
                  {/*  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>*/}
                  {/*  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>*/}
                  {/*</svg>*/}
                  Upload Face
                </button>
              )}

              <button
                onClick={startCamera}
                className="flex-1 bg-gray-800 text-white dark:bg-golden dark:text-black px-4 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-golden transition-colors flex items-center justify-center gap-2 text-sm"
              >
                {/*<img*/}
                {/*    src={camera}*/}
                {/*    alt=""*/}
                {/*    className="h-[24px] w-[24px]"*/}
                {/*/>*/}
                Take Selfie
              </button>
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="relative mb-6">
              <img
                src={previewUrl!}
                alt="Selected"
                className="w-48 h-48 object-cover rounded-lg shadow-md"
              />
              <button
                onClick={handleClearSelection}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg hover:bg-red-600 transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={handleClearSelection}
                className="flex-1 border border-gray-300 text-black dark:border-golden dark:text-golden px-4 py-2 rounded-lg hover:border-gray-600 transition-colors font-medium text-sm"
              >
                Choose Different Selfie
              </button>

              <button
                onClick={handleSearch}
                disabled={aiSearchData.loading}
                className="flex-1 bg-gray-800 text-white dark:bg-golden dark:text-black px-4 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-golden transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {aiSearchData.loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </span>
                ) : (
                  'Search Photos'
                )}
              </button>
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default AISearchInterface